import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { HelpdeskDtoKeyEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk-dto-key.enum';
import { NotificationDtoKeysEnum } from '@zaoblako/shared/modules/notification/enums/notification-dto-keys.enum';
import { BusinessDtoKeysEnum } from '@zaoblako/shared/modules/business/enum/business-dto-keys.enum';
import { MessengerDtoKeysEnum } from '@zaoblako/shared/modules/messenger/enums/messenger-dto-keys.enum';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import { booleanSchema } from '@zaoblako/shared/modules/serde/schemas/boolean.schema';
import type { ReminderFindInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder-find.dto';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import { type HelpdeskEmailFindInterface, HelpdeskEmailFindDto } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-email-find.dto';
import { type HelpdeskTaskStatusFindInterface, HelpdeskTaskStatusFindDto } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-status-find.dto';
import { type UserFindInterface, UserFindDto } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { ThreadFindInterface } from '@zaoblako/shared/modules/messenger/dtos/thread/thread-find.dto';
import { type HelpdeskTaskTemplateInterface, HelpdeskTaskTemplateDto } from '@zaoblako/shared/modules/helpdesk/dto/template/helpdesk-task-template.dto';
import { type HelpdeskTaskTypeFindInterface, HelpdeskTaskTypeFindDto } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import { type UnitFindInterface, UnitFindDto } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import { type ExternalUserFindInterface, ExternalUserFindDto } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import { type ClientFindInterface, ClientFindDto } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import { type ProjectFindInterface, ProjectFindDto } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import { type UserGroupFindInterface, UserGroupFindDto } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { type MasterMilestoneFindInterface, MasterMilestoneFindDto } from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-find.dto';
import { EavEntityFindDto, type EavEntityFindInterface } from '@zaoblako/shared/modules/eav/dtos/entity/eav-entity-find.dto';

import * as yup from 'yup';

export interface HelpdeskTaskFindInterface {
  id: number;
  number: number;
  created: Date;
  finishedAt: Date;
  factMin?: number;
  status: HelpdeskTaskStatusFindInterface;
  createdBy?: UserFindInterface;
  email: HelpdeskEmailFindInterface;
  isFavorite?: boolean;
  expired?: boolean;
  daysAgoCreated?: number;
  hoursAgoCreated?: number;
  thread?: ThreadFindInterface;
  filesCount?: number;
  reminder?: ReminderFindInterface;
  template?: HelpdeskTaskTemplateInterface;
  deadline: Date;
  plannedMin: number;
  description: string;
  project: ProjectFindInterface;
  deal: DealFindInterface;
  client?: ClientFindInterface;
  externalUser?: ExternalUserFindInterface;
  responsible: UserFindInterface;
  taskType: HelpdeskTaskTypeFindInterface;
  unit: UnitFindInterface;
  priority: HelpdeskTaskPriorityEnum;
  userAuditors: UserFindInterface[];
  userGroupAuditors: UserGroupFindInterface[];
  externalUserAuditors?: ExternalUserFindInterface[];
  masterMilestone?: MasterMilestoneFindInterface;
  eavEntity?: EavEntityFindInterface;
  aiAnswer?: boolean;
}

export const HelpdeskTaskFindDto = SerdeDtoClass.create<HelpdeskTaskFindInterface>(
  HelpdeskDtoKeyEnum.HELPDESK_TASK_FIND_DTO,
  {
    id: {
      sortable: true,
      label: 'Номер в ФРМ',
      serializer: yup.number(),
    },
    number: {
      label: 'Внутренний номер',
      sortable: true,
      serializer: yup.number(),
    },
    created: {
      sortable: true,
      label: 'Создан',
      serializer: yup.date(),
    },
    factMin: {
      sortable: true,
      optional: true,
      label: 'Фактическое затраченное время в минутах',
      serializer: yup.number(),
    },
    status: {
      sortable: true,
      label: 'Статус',
      nested: HelpdeskTaskStatusFindDto,
      serializer: (_, nested) => nested,
    },
    finishedAt: {
      label: 'Завершён',
      serializer: yup.date(),
    },
    isFavorite: {
      optional: true,
      label: 'Главный',
      serializer: yup.boolean(),
    },
    createdBy: {
      optional: true,
      label: 'Автор',
      nested: UserFindDto,
      serializer: (_, nested) => nested,
    },
    email: {
      label: 'Автор',
      nested: HelpdeskEmailFindDto,
      serializer: (_, nested) => nested,
    },
    expired: {
      label: 'Истёкший',
      serializer: yup.boolean(),
    },
    daysAgoCreated: {
      label: 'Создано дней назад',
      serializer: yup.number(),
    },
    hoursAgoCreated: {
      label: 'Создано часов назад',
      serializer: yup.number(),
    },
    thread: {
      optional: true,
      label: 'thread',
      nested: MessengerDtoKeysEnum.THREAD_FIND_DTO,
      serializer: (_, nested) => nested,
    },
    filesCount: {
      optional: true,
      label: 'Количество файлов',
      serializer: yup.number(),
    },
    reminder: {
      optional: true,
      label: 'Напоминание',
      nested: NotificationDtoKeysEnum.REMINDER_FIND_DTO,
      serializer: (_, nested) => nested,
    },
    template: {
      optional: true,
      label: 'Шаблон',
      nested: HelpdeskTaskTemplateDto,
      serializer: (_, nested) => nested,
    },
    deadline: {
      sortable: true,
      label: 'Дедлайн',
      serializer: yup.date(),
    },
    plannedMin: {
      sortable: true,
      label: 'Запланированное время',
      serializer: yup.number(),
    },
    description: {
      label: 'Описание',
      serializer: yup.string(),
    },
    project: {
      label: 'Проект',
      nested: ProjectFindDto,
      serializer: (_, nested) => nested,
    },
    deal: {
      label: 'Сделка',
      nested: BusinessDtoKeysEnum.DEAL_FIND_DTO,
      serializer: (_, nested) => nested,
    },
    client: {
      sortable: true,
      optional: true,
      label: 'Клиент',
      nested: ClientFindDto,
      serializer: (_, nested) => nested,
    },
    externalUser: {
      sortable: true,
      optional: true,
      label: 'Автор (внешний)',
      nested: ExternalUserFindDto,
      serializer: (_, nested) => nested,
    },
    responsible: {
      sortable: true,
      label: 'Ответственный',
      nested: UserFindDto,
      serializer: (_, nested) => nested,
    },
    taskType: {
      sortable: true,
      label: 'Тип',
      nested: HelpdeskTaskTypeFindDto,
      serializer: (_, nested) => nested,
    },
    unit: {
      sortable: true,
      label: 'БЕ',
      nested: UnitFindDto,
      serializer: (_, nested) => nested,
    },
    priority: {
      sortable: true,
      label: 'Приоритет',
      serializer: yup
        .string()
        .oneOf(Object.values(HelpdeskTaskPriorityEnum)),
    },
    userAuditors: {
      label: 'Аудиторы',
      nested: UserFindDto,
      serializer: (_, nested) => yup.array(nested),
    },
    userGroupAuditors: {
      label: 'Группы аудиторов',
      nested: UserGroupFindDto,
      serializer: (_, nested) => yup.array(nested),
    },
    externalUserAuditors: {
      optional: true,
      label: 'Внешние аудиторы',
      nested: ExternalUserFindDto,
      serializer: (_, nested) => yup.array(nested),
    },
    masterMilestone: {
      sortable: true,
      optional: true,
      label: 'Мастер-веха',
      nested: MasterMilestoneFindDto,
      serializer: (_, nested) => nested,
    },
    eavEntity: {
      optional: true,
      label: 'Дополнительные поля',
      nested: EavEntityFindDto,
      serializer: (_, nested) => nested,
    },
    aiAnswer: {
      optional: true,
      label: 'AI ответ',
      serializer: booleanSchema,
    },
  },
);
